import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { translateObject } from '@jsverse/transloco';

import { AppSharedModule } from 'app/app-shared.module';
import { SimpleDialogButtonColor } from 'app/components/simple-dialog.component';
import { AuthService, Session } from 'app/providers/bridge-api/auth.service';
import { Dashboard, DashboardService } from 'app/providers/bridge-api/dashboard.service';
import { SemantriaUsersService } from 'app/providers/semantria-api/semantria-users.service';
import { AlertService } from 'app/services/alert.service';
import { DialogService } from 'app/services/dialog.service';
import {
  EnableClassTelemetry,
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from 'app/services/otel.service';

interface DashboardRevision {
  revision: number;
  timestamp: Date;
  user: string;
  isRestored?: boolean;
}

@EnableClassTelemetry()
@Component({
  selector: 'app-dashboard-option-history',
  imports: [AppSharedModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './dashboard-option-history.component.html',
  styleUrls: ['./dashboard-option-history.component.scss'],
})
export class DashboardOptionHistoryComponent implements OnInit, OnChanges {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();

  @Input() dashboard: Dashboard;
  @Input() session: Session;

  @Output() restoreDashboard = new EventEmitter<Dashboard>();

  displayedColumns = ['revision', 'timestamp', 'user'];
  dataSource = new MatTableDataSource<DashboardRevision>([]);
  usersById = new Map<string, string>();

  canRestore;

  private dashboardService = inject(DashboardService);
  private userService = inject(SemantriaUsersService);
  private authService = inject(AuthService);
  private dialogService = inject(DialogService);
  private alertService = inject(AlertService);

  ngOnInit(): void {
    this.canRestore = !this.authService.isReadOnly;
    if (this.canRestore) {
      this.displayedColumns.push('actions');
    }

    this.dashboardService
      .getDashboardRevisions(this.dashboard.project_id, this.dashboard.id)
      .then((res) => {
        this.dataSource.data = res.result.hits
          .map((d) => ({
            revision: d.revision,
            timestamp: new Date(d.modified_at_date),
            user: d.user_id,
          }))
          .sort((d1, d2) => d2.timestamp.getTime() - d1.timestamp.getTime());

        return [...new Set(this.dataSource.data.map((d) => d.user))];
      })
      .then((users) => {
        for (const user of users) {
          if (!this.usersById.has(user)) {
            this.userService.getUser(user).then((res) => {
              this.usersById.set(user, this.getUserDisplayName(res));
            });
          }
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.session?.currentValue) {
      this.usersById.set(this.session.user.id, this.getUserDisplayName(this.session.user));
    }
  }

  private getUserDisplayName(user) {
    return ((user.first_name || '') + ' ' + (user.last_name || '')).trim() || user.email;
  }

  onRestoreRevision(revision) {
    if (!this.canRestore) {
      return;
    }

    const translations = translateObject(
      'dashboard-options-component.dashboard-option-history.restore-dialog',
      {
        'restore-successful': {
          version: revision,
        },
      }
    );
    this.dialogService
      .showSimpleDialog({
        title: translations['title'],
        content: translations['content'],
        confirmTitle: translations['restore'],
        confirmColor: SimpleDialogButtonColor.orange,
        cancelTitle: translations['cancel'],
      })
      .afterClosed()
      .subscribe(async (result) => {
        if (result) {
          try {
            const restoredDashboard = await this.dashboardService.restoreDashboardRevision(
              this.dashboard.project_id,
              this.dashboard.id,
              revision
            );
            this.dataSource.data.unshift({
              revision: restoredDashboard.revision,
              timestamp: new Date(restoredDashboard.modified_at_date),
              user: restoredDashboard.user_id,
              isRestored: true,
            });
            if (this.dataSource.data.length > 10) {
              this.dataSource.data.splice(-1, 1);
            }
            this.dataSource.data = [...this.dataSource.data];
            this.restoreDashboard.emit(restoredDashboard);

            this.alertService.showToast(translations['restore-successful']);
          } catch (error) {
            this.alertService.showErrorToast(translations['restore-failed'], error);
          }
        }
      });
  }
}
